import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import PaginationElement from '@/views/elements/pagination/PaginationElement';
import { Stack, TableHead, TableSortLabel, useTheme, Box, styled, } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import TypographyElement from '@/views/elements/typography/TypographyElement';
import { ResponseSuccess, ResponseSuccessPagination } from '@/controllers/gridManager/followerTransManager/followerTransManager';
import { isEmpty } from 'lodash';
import ViewNumberFormat from '@/views/elements/Inputs/ViewNumberFormat';
import dayjs, { Dayjs } from 'dayjs';
import { formatDate } from '@/utilities';
import Formula from '@hapi/formula';
import CheckboxElement from '@/views/elements/checkBox/CheckBox';
import { produce } from 'immer';
import { getInstrumentsBySymbolsNoSave, getQuoteSymbolNoSave } from '@/controllers/gridManager/datafeedManager/datafeedManager';
import ButtonElement from '@/views/elements/button/ButtonElement';
import { KeyDataParents } from '../CopyTrade/Follower/SellStuckStocks/SellStuckStocks';
import PriceInput from '@/views/elements/Inputs/PriceInput';
import QuantityInput from '@/views/elements/Inputs/QuantityInput';
import ToastUtil from '../Toast/Toast';
import { useTranslation } from 'react-i18next';

export interface Column {
    id: string
    label: string;
    enableSort?: boolean
    minWidth?: number;
    width?: number;
    align?: 'right' | 'left' | 'center';
    alignCell?: 'right' | 'left' | 'center';
    format?: (value: number) => string;
    formatNumber?: boolean,
    isPercentage?: boolean,
    isCheckPNL?: boolean, // check màu lãi lỗ
    // customRender
    inputRender?: boolean
    cellFormula?: any//'sum' | 'avg'
    isOddLot?: boolean
}

export type Quote = {
    symbol: string
    ceiling: number,
    floor: number,
    reference: number,
    exchange: string,
    tradelot: number,
    pricestep: number,
    FullName: string,
    StockType: string,
}

type InputValid = {
    priceInput: {
        value: number
        isValid: boolean
    }
    qttyInput: {
        value: number
        isValid: boolean
    }
}

type ObjectQuotes = { [key: string]: Quote }

export default function TableUIActionInputElement({ columns, fnCallApi, objParameterFn,
    enableDatePicker, listenerQuery,
    defaultOrderBy, defaultSort, supportCheckBox, rowKey,
    setSelectedDataToParent, prevData, supportPagination, isSupportInstrument,
    actionArea, close, next,
}: {
    enableDatePicker?: boolean, columns: Column[],
    fnCallApi: (...args: any) => Promise<ResponseSuccess>,
    objParameterFn?: Partial<Parameters<typeof fnCallApi>[0]>,
    queryBeforeDate?: () => React.ReactNode,
    queryAfterDate?: () => React.ReactNode,
    listenerQuery?: any,
    defaultOrderBy?: string
    defaultSort?: 'asc' | 'desc',

    supportCheckBox?: boolean
    rowKey: string //supportCheckBox = true => require rowKey
    setSelectedDataToParent?: (key: KeyDataParents, data: {}) => void //supportCheckBox = true => require rowKey
    prevData?: any
    supportPagination?: boolean
    isSupportInstrument?: boolean
    actionArea?: boolean
    close: () => void
    next: () => void
}) {

    const theme = useTheme()
    const { t } = useTranslation()

    const [rows, setRows] = React.useState<any[]>([]);
    const [cellInputValue, setCellInputValue] = React.useState<{
        [k: string]: InputValid
    }>({});

    const [order, setOrder] = React.useState<Order>(defaultSort ?? 'asc');
    const [orderBy, setOrderBy] = React.useState<string>(defaultOrderBy ?? 'size');

    const [total, setTotal] = React.useState<number>(25)
    const [pageSize, setPageSize] = React.useState<number>(20)
    const [pageIndex, setPageIndex] = React.useState<number>(1)

    const [toDate, setToDate] = React.useState<Dayjs | null>(dayjs(new Date()));
    const [fromDate, setFromDate] = React.useState<Dayjs | null>(dayjs(toDate).subtract(1, 'M'));

    const [selected, setSelected] = React.useState<readonly string[]>([]);

    const [quotes, setQuotes] = React.useState<ObjectQuotes>({});

    const handleChangeSetToDate = (newValue: Dayjs | null) => {
        setPageIndex(1);
        setToDate(newValue)
    }

    const handleChangeSetFromDate = (newValue: Dayjs | null) => {
        setPageIndex(1);
        setFromDate(newValue)
    }

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        const nPage = newPage + 1
        console.log('ha_check_newPage', nPage);
        setPageIndex(nPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setPageSize(parseInt(event.target.value, 10));
        setPageIndex(1);
    };

    const safePage = (index: number) => {
        return (index - 1) < 0 ? 0 : (index - 1)
    }

    const handleSelectTable = (newSelected: any) => {
        setSelected(newSelected)
        console.log('ha_check_setSelectedDataToParent', newSelected)
    }

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (rowKey && supportCheckBox === true) {
            if (event.target.checked) {
                const newSelected = rows.map((n) => n[rowKey]);
                console.log('ha_check_handleSelectAllClick', newSelected)
                handleSelectTable(newSelected);
                return;
            }
            handleSelectTable([]);
        }
    };

    const handleClick = (event: React.MouseEvent<unknown>, id: string) => {
        if (rowKey && supportCheckBox === true) {
            const selectedIndex = selected.indexOf(id);
            let newSelected: readonly string[] = [];

            if (selectedIndex === -1) {
                newSelected = newSelected.concat(selected, id);
            } else if (selectedIndex === 0) {
                newSelected = newSelected.concat(selected.slice(1));
            } else if (selectedIndex === selected.length - 1) {
                newSelected = newSelected.concat(selected.slice(0, -1));
            } else if (selectedIndex > 0) {
                newSelected = newSelected.concat(
                    selected.slice(0, selectedIndex),
                    selected.slice(selectedIndex + 1),
                );
            }
            handleSelectTable(newSelected);
        }
    };


    React.useEffect(() => {
        async function callApiGetRowsData() {
            const injectParam: Parameters<typeof fnCallApi>[0] = {
                ...objParameterFn,
                fromDate: formatDate(fromDate),
                toDate: formatDate(toDate),
                pageIndex: objParameterFn?.pageIndex ?? pageIndex,
                pageSize: objParameterFn?.pageSize ?? pageSize,
            }
            const { isSuccess, data } = await fnCallApi(!isEmpty(injectParam) ? injectParam : {})
            const rs = data as ResponseSuccessPagination

            console.log('ha_check_callApiGetRowsData', { isSuccess, data });
            if (isSuccess) {
                setRows(rs.data)
                setPageSize(rs.pageSize)
                setTotal(rs.total)
                setPageIndex(rs.pageIndex)
            }
        }

        if (pageSize === 0) return;

        callApiGetRowsData()
    }, [pageSize, pageIndex, fromDate, toDate, listenerQuery])

    React.useEffect(() => {
        const listSymbols = rows.map((item: any) => {
            return item.symbol
        })

        async function callApiGetInstrument() {
            const { isSuccess, data } = await getInstrumentsBySymbolsNoSave({ config: {}, symbols: listSymbols })
            if (isSuccess) {
                // console.log('ha_check_datacallApiGetInstrument', data, rows)
                const rs = rows.map((item: any) => {
                    const findInstrument = data.find((instrument: any) => item.symbol === instrument.symbol)
                    return { symbol: item.symbol, qttyInput: item.qtty, priceInput: findInstrument?.closePrice === 0 ? item.price : findInstrument?.closePrice ?? item.price }
                })

                const Obj: any = {}
                for (const element of rs) {
                    Obj[element.symbol] = {
                        qttyInput: { value: element.qttyInput, isValid: true },
                        priceInput: { value: element.priceInput, isValid: true },
                    }
                }

                console.log('ha_check_datacallApiGetInstrument1', Obj)
                setCellInputValue(Obj);
            }
        }

        async function callApiGetQuoteSymbol() {
            const { isSuccess, data } = await getQuoteSymbolNoSave({ config: {}, symbols: listSymbols })
            if (isSuccess) {
                console.log('ha_check_data', cellInputValue)
                const Obj: ObjectQuotes = {}
                for (const element of data) {
                    Obj[element.symbol] = {
                        ...element
                    }
                }
                setQuotes(Obj)

                if (prevData?.isSaved === true) {
                    setSelected(prevData?.listSelected ?? [])
                    setCellInputValue(prevData?.cellInputValue)
                }
            }
        }

        if (isSupportInstrument === true && listSymbols.length !== 0) {
            callApiGetInstrument().then(() => {
                callApiGetQuoteSymbol()
            })
        }
    }, [rows])

    const StyledTableCell = styled(TableCell)(({ theme }) => ({
        [`&.${tableCellClasses.head}`]: {
            backgroundColor: theme.palette.table?.header,
            color: theme.palette.common.white,
        },
        [`&.${tableCellClasses.body}`]: {
            fontSize: 14,
        },
    }));

    // const StyledTableRow = styled(TableRow)(({ theme }) => ({
    //     '&:nth-of-type(odd)': {
    //         backgroundColor: "#1F2329",
    //     },
    //     // hide last border
    //     '&:last-child td, &:last-child th': {
    //         border: 0,
    //     },

    // }));

    interface EnhancedTableProps {
        onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
        order: Order;
        orderBy: string;

        numSelected: number;
        onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
        rowCount: number;
    }

    function EnhancedTableHead(props: EnhancedTableProps) {
        const { order, orderBy, onRequestSort, onSelectAllClick, rowCount, numSelected } =
            props;
        const createSortHandler =
            (property: string) => (event: React.MouseEvent<unknown>) => {
                onRequestSort(event, property);
            };

        return (
            <TableHead>
                <TableRow>
                    {supportCheckBox === true && rowKey && (
                        <StyledTableCell padding="checkbox">
                            <CheckboxElement
                                color="primary"
                                indeterminate={numSelected > 0 && numSelected < rowCount}
                                checked={rowCount > 0 && numSelected === rowCount}
                                onChange={onSelectAllClick}
                                inputProps={{
                                    'aria-label': 'select all desserts',
                                }}
                            />
                        </StyledTableCell>
                    )}
                    {columns.map((headCell) => (
                        <StyledTableCell
                            key={headCell.id}
                            sortDirection={orderBy === headCell.id ? order : false}
                            align={headCell.align}
                            style={{ minWidth: headCell.minWidth, width: headCell.width }}
                            sx={{ padding: "12px 16px" }}
                        >
                            {headCell.enableSort === true ? <TableSortLabel
                                active={orderBy === headCell.id}
                                direction={orderBy === headCell.id ? order : 'asc'}
                                onClick={createSortHandler(headCell.id)}
                            >
                                <TypographyElement TextAlignElement={headCell.align} FontSizeElement='12px' FontWeightElement={600}>{headCell.label}</TypographyElement>
                                {orderBy === headCell.id ? (
                                    <Box component="span" sx={visuallyHidden}>
                                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                    </Box>
                                ) : null}
                            </TableSortLabel> : (
                                <TypographyElement TextAlignElement={headCell.align} FontSizeElement='12px' FontWeightElement={600}>{headCell.label}</TypographyElement>
                            )}
                        </StyledTableCell>
                    ))}
                </TableRow>
            </TableHead>
        );
    }

    function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
        if (b[orderBy] < a[orderBy]) {
            return -1;
        }
        if (b[orderBy] > a[orderBy]) {
            return 1;
        }
        return 0;
    }

    type Order = 'asc' | 'desc';

    function getComparator<Key extends keyof any>(
        order: Order,
        orderBy: Key,
    ): (
        a: { [key in Key]: number | string },
        b: { [key in Key]: number | string },
    ) => number {
        return order === 'desc'
            ? (a, b) => descendingComparator(a, b, orderBy)
            : (a, b) => -descendingComparator(a, b, orderBy);
    }

    const handleRequestSort = (
        event: React.MouseEvent<unknown>,
        property: string,
    ) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const visibleRows = React.useMemo(
        () =>
            [...rows]
                .sort(getComparator(order, orderBy)),
        [rows, order, orderBy, pageIndex, pageSize],
    );

    const renderInput = (key: string, field: keyof InputValid, isOddLot: boolean, rowData: any) => {
        console.log('ha_check_renderInput', { cellInputValue, key, field, rs: cellInputValue?.[key]?.[field] })

        if (field === 'priceInput') {
            return <PriceInput
                align="right"
                realValue={cellInputValue?.[key]?.[field]?.value}
                handleChangeInput={(e, isValid) => handleChangeInput(e, key, field, isValid)}
                handleChangeValid={(isValid) => handleChangeValid(key, field, isValid)}

                symbol={key}
                quote={quotes[key]}
                error={cellInputValue?.[key]?.[field]?.isValid === false}
                placementTooltip="right"
            />
        } else if (field === 'qttyInput') {
            return <QuantityInput
                align="right"
                realValue={cellInputValue?.[key]?.[field]?.value}
                handleChangeInput={(e, isValid) => handleChangeInput(e, key, field, isValid)}
                handleChangeValid={(isValid) => handleChangeValid(key, field, isValid)}
                maxValue={rowData.qtty}

                symbol={key}
                quote={quotes[key]}
                error={cellInputValue?.[key]?.[field]?.isValid === false}
                placementTooltip="left"
                isOddLot={isOddLot}
            />
        } else {
            return null
        }
    }

    const handleChangeValid = (key: string, field: string, isValid: boolean) => {
        setCellInputValue(produce((draft: { [k: string]: InputValid }) => {
            if (!draft[key]) {
                draft[key] = {
                    priceInput: { value: 0, isValid: true },
                    qttyInput: { value: 0, isValid: true },
                }; // Initialize the object if it's undefined
            }
            draft[key][field as keyof InputValid].isValid = isValid
        }));
    };

    const handleChangeInput = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, key: string, field: string, isValid: boolean) => {

        setCellInputValue(produce((draft: { [k: string]: InputValid }) => {
            if (!draft[key]) {
                draft[key] = {
                    priceInput: { value: 0, isValid: true },
                    qttyInput: { value: 0, isValid: true },
                }; // Initialize the object if it's undefined
            }
            draft[key][field as keyof InputValid].value = Number.parseFloat(event.target.value);
            draft[key][field as keyof InputValid].isValid = isValid
        }));
    };

    const cellRender = (column: Column, row: any) => {
        const value = row[column.id];
        const id = column.id

        if (column?.cellFormula) {
            const formula = new Formula.Parser(column?.cellFormula);
            let dataFromInput: any = {}
            const arrInput = columns.filter((item: Column) => {
                return item.inputRender === true
            })

            for (const element of arrInput) {
                console.log('ha_check_calculatorValue', cellInputValue, row, rowKey)

                dataFromInput[element.id] = cellInputValue?.[row[rowKey]]?.[element.id as keyof InputValid]?.value
            }

            const calculatorValue = formula.evaluate({ ...row, ...dataFromInput })
            console.log('ha_check_calculatorValue', cellInputValue, dataFromInput, row, calculatorValue, column?.cellFormula, { ...row, ...dataFromInput })
            return <TableCell sx={{ padding: "12px 16px" }}
                key={id} align={column.alignCell} >
                {
                    <ViewNumberFormat value={calculatorValue} displayType='text' />
                }
            </TableCell >
        }

        const ids = id.split('|')
        const joinValue = ids?.length > 1 ? ids.map(item => `${row[item]}`).join(' | ') : ''

        if (column?.inputRender === true) {
            console.log('ha_check_row', row)
            return <TableCell sx={{ padding: "12px 16px" }}
                key={id} align={column.alignCell} >
                {
                    renderInput(row[rowKey], id as keyof InputValid, column?.isOddLot ?? false, row)
                }
            </TableCell >
        }

        return (
            <TableCell sx={{ padding: "12px 16px" }}
                key={id} align={column.alignCell} >
                {
                    column.formatNumber === true ? (
                        column?.isCheckPNL === true ? (
                            <TypographyElement TextAlignElement={column.alignCell ?? 'end'} ColorElement={value > 0 ? "up" : "down"}>
                                <ViewNumberFormat value={value} displayType='text' isPercentage={column?.isPercentage === true} />
                            </TypographyElement>
                        ) :
                            <ViewNumberFormat value={value} displayType='text' isPercentage={column?.isPercentage === true} />
                    ) : ids?.length > 1 ? joinValue : value
                }
            </TableCell >
        )
    }

    React.useEffect(() => {
        console.log('ha_check_cellInputValue', cellInputValue)
    }, [cellInputValue])

    const handleNext = () => {
        if (selected.length === 0) {
            ToastUtil.error(t('copytrade.followerHistoryTransactions.noChoose'))
        } else {
            let flag = true

            for (const element of selected) {
                if (cellInputValue[element].priceInput.isValid === false || cellInputValue[element].qttyInput.isValid === false) {
                    flag = false;
                    break;
                }
            }

            if (flag === true) {
                const selectedData = {
                    rows, cellInputValue, listSelected: selected, isSaved: true
                }
                setSelectedDataToParent && setSelectedDataToParent("Step1", selectedData);
                next()
            } else {
                ToastUtil.error(t('copytrade.followerHistoryTransactions.checkChoose'))
            }
        }
    }

    return (
        <Stack spacing={1} >
            <Box height={400}>
                <TableContainer sx={{ height: enableDatePicker === true ? "calc(100% - 50px)" : "100%" }}>
                    <Table stickyHeader aria-label="sticky table" size='small'>
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            onSelectAllClick={handleSelectAllClick}
                            //check it selected
                            numSelected={selected?.length ?? 0}
                            rowCount={rows.length}
                        />
                        <TableBody>

                            {visibleRows
                                .map((row) => {
                                    const isItemSelected = (supportCheckBox === true && rowKey) ? selected?.includes(row[rowKey]) : false;

                                    return (
                                        <TableRow sx={{
                                            '&:nth-of-type(odd)': {
                                                backgroundColor: theme.palette.table?.odd,
                                            },
                                            // hide last border
                                            '&:last-child td, &:last-child th': {
                                                border: 0,
                                            },

                                        }} tabIndex={-1} key={row.code}>
                                            {supportCheckBox === true && rowKey && (
                                                <TableCell padding="checkbox">
                                                    <CheckboxElement
                                                        color="primary"
                                                        onClick={(event: React.MouseEvent<unknown>) => handleClick(event, row[rowKey])}
                                                        checked={isItemSelected}
                                                    // inputProps={{
                                                    //     'aria-labelledby': labelId,
                                                    // }}
                                                    />
                                                </TableCell>
                                            )}
                                            {columns.map((column) => {
                                                return cellRender(column, row)
                                            })}
                                        </TableRow>
                                    );
                                })}

                        </TableBody>
                    </Table>
                </TableContainer>


                {(!(supportPagination === false)) && <PaginationElement
                    component="div"
                    count={total}
                    page={safePage(pageIndex)}
                    onPageChange={handleChangePage}
                    rowsPerPage={pageSize}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />}
            </Box>

            {actionArea === true && <Stack direction={'row'}
                spacing={1}
            >
                <ButtonElement customType="Secondary" onClick={close} fullWidth={true} > Đóng</ButtonElement>
                <ButtonElement customType="Primary" onClick={handleNext} fullWidth={true} > Tiếp tục</ButtonElement>
            </Stack>}
        </Stack>
    );
}
